import type React from 'react'
import { useEffect, useState } from 'react'
import {
  AddEPLITrainerDevelopmentPlan,
  GetTrainings,
  UpdateEPLITrainerDevelopmentPlan
} from '../../../services/AnnualPlanService'
import { toast } from 'react-toastify'
import { Modal } from '../../customComponents/Modal'
import {
  Alert,
  Box,
  CircularProgress,
  FormControl, FormControlLabel, FormLabel, InputAdornment,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  type SelectChangeEvent, Stack, TextField,
  Typography
} from '@mui/material'
import { EventDisplay } from '../../customComponents/EventDisplay'
import { ActivityEditSession } from './priorityActivities/ActivityEditSession'
import { PriorityOneActivityName } from '../../../models/PriorityOneActivityName'
import { TrainingType, type Training } from '../../../models/Training'
import { EPLITrainerStatus } from '../../../models/EPLITrainerStatus'
import { usePlanOrganizationOrgs } from '../../../hooks/use-planOrganizationOrgs'
import { isValidPositiveNumber } from '../../../utils/isValidNumber'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { BudgetThresholdExceededModal } from './BudgetExceededModal'
import { budgetNeedsReview } from '../../../utils/budgetUtil'

export interface AddEPLITrainerDevelopmentPlanFormValues {
  priorityId: number
  epliTrainingId: number | null
  numberOfAttendees: string | number | null
  budgetRequest: string | number | null
  isImplementationPhase?: boolean
  attendees?: AttendeeFormValues[]
}
export interface UpdateEPLITrainerDevelopmentPlanFormValues extends AddEPLITrainerDevelopmentPlanFormValues {
  id: number
  isUpdate?: boolean
  actualAmount?: number | null
}

export interface AttendeeFormValues {
  id?: number
  fullName: string
  isAttendeeFromDistrict: boolean | null
  isdId: number | null
  districtId?: number | null
  status: EPLITrainerStatus | null
  completionStatus?: string | null
}

export interface AdditionalDetailsFormValues {
  attendees: AttendeeFormValues[]
}

interface Props {
  open: boolean
  annualPlanId: number
  orgId: number
  priorityId: number
  initialValues?: UpdateEPLITrainerDevelopmentPlanFormValues
  onClose: (isUpdate: boolean) => void
  onSaved: () => void
  implementationPhase?: boolean
  startOnDetailsPage?: boolean
  planOrgId: number
  usedIds: number[]
}

type ConfirmState = 'none' | 'needsConfirmation' | 'confirmed'

export const EPLITrainerDevelopmentPlanModal = (props: Props): JSX.Element => {
  const isEditing = props.initialValues != null
  const [confirmState, setConfirmState] = useState<ConfirmState>('none')
  const [formValues, setFormValues] = useState<AddEPLITrainerDevelopmentPlanFormValues>({
    priorityId: props.priorityId ?? 0,
    epliTrainingId: null,
    numberOfAttendees: null,
    budgetRequest: null
  })
  const [submitted, setIsSubmitted] = useState(false)
  const [epliTrainingOptions, setEPLITrainingOptions] = useState<Training[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [modalPage, setModalPage] = useState<'edit' | 'details'>(props.startOnDetailsPage === true ? 'details' : 'edit')
  const [showCancelUpdatesModal, setShowCancelUpdatesModal] = useState<boolean>(false)

  const [additionalDetailsFormValues, setAdditionalDetailsFormValues] = useState<AdditionalDetailsFormValues>({
    attendees: props.initialValues?.attendees ?? []
  })
  const [additionalDetailsSubmitted, setAdditionalDetailsSubmitted] = useState(false)
  const { isLoading: isLoadingPlanOrgOrgs, planOrganizationOrgs } = usePlanOrganizationOrgs(props.planOrgId)
  const isd = planOrganizationOrgs?.isd
  const districts = planOrganizationOrgs?.districts

  const attendeeFormValidator = (index: number): Record<keyof AttendeeFormValues, { required: boolean, isValid: boolean }> => ({
    id: { required: false, isValid: true },
    isAttendeeFromDistrict: { required: true, isValid: true },
    fullName: { required: true, isValid: additionalDetailsFormValues.attendees[index]?.fullName.trim().length > 0 },
    isdId: { required: true, isValid: isValidPositiveNumber(additionalDetailsFormValues.attendees[index]?.isdId) },
    districtId: { required: additionalDetailsFormValues.attendees[index]?.isAttendeeFromDistrict === true, isValid: isValidPositiveNumber(additionalDetailsFormValues.attendees[index]?.districtId) },
    status: { required: true, isValid: additionalDetailsFormValues.attendees[index]?.status != null && Object.values(EPLITrainerStatus).includes(additionalDetailsFormValues.attendees[index].status?.trim() as EPLITrainerStatus) },
    completionStatus: { required: false, isValid: true }
  })
  const additionalDetailsFormValidators: Record<keyof AdditionalDetailsFormValues, { required: boolean, isValid: boolean }> = {
    attendees: {
      required: true,
      isValid: additionalDetailsFormValues.attendees.every((_, index) => Object.values(attendeeFormValidator(index)).every((a) => (a.required && a.isValid) || !a.required))
    }
  }
  const hasInvalidAdditionalDetailsFormValues = Object.values(additionalDetailsFormValidators).some(v => v.required && !v.isValid)

  useEffect(() => {
    const fetchEPLITrainingOptions = async (): Promise<void> => {
      setIsLoading(true)
      const trainings = await GetTrainings(TrainingType.EPLI)
      const filteredTrainings = trainings.filter(t => !props.usedIds.includes(t.id ?? -1) || (props.initialValues != null && t.id === props.initialValues.epliTrainingId))

      setEPLITrainingOptions(filteredTrainings)
      setIsLoading(false)
    }

    void fetchEPLITrainingOptions()
  }, [])

  useEffect(() => {
    if (props.initialValues != null) {
      setFormValues(props.initialValues)
    }
  }, [props.initialValues])

  const formValidators: Record<keyof AddEPLITrainerDevelopmentPlanFormValues, { required: boolean, isValid: boolean }> = {
    priorityId: { required: true, isValid: true },
    epliTrainingId: { required: true, isValid: (formValues.epliTrainingId != null && formValues.epliTrainingId > 0) },
    numberOfAttendees: { required: true, isValid: isValidPositiveNumber(formValues.numberOfAttendees) },
    budgetRequest: { required: true, isValid: isValidPositiveNumber(formValues.budgetRequest) },
    isImplementationPhase: { required: false, isValid: true },
    attendees: { required: false, isValid: true }
  }

  const invalidFormValues = Object.entries(formValidators)
    .filter(([_, validator]) => validator.required && !validator.isValid)
    .map(([fieldName, _]) => fieldName)

  const handleFieldUpdate = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target
    setFormValues({ ...formValues, [name]: value })
  }

  const handleSubmit = async (): Promise<void> => {
    if (invalidFormValues.length > 0) {
      if (invalidFormValues.includes('budgetRequest')) {
        if (invalidFormValues.length === 1) {
          toast.error('Budget must be greater than or equal to 0')
        } else {
          toast.error('Please fill out required fields and ensure budget is greater than or equal to 0')
        }
        return
      } else {
        toast.error('Please fill out required fields')
        return
      }
    }

    if (props.initialValues != null && modalPage === 'edit' && props.initialValues.isUpdate === false) {
      if (budgetNeedsReview(props.initialValues.budgetRequest, formValues.budgetRequest)) {
        setConfirmState('needsConfirmation')
        return
      }
    }

    await continueSubmit()
  }

  const continueSubmit = async (): Promise<void> => {
    setConfirmState('none')

    if (props.implementationPhase === true) {
      if (modalPage === 'edit') {
        setIsSubmitted(true)
        setModalPage('details')
        // Initialize additional details form values based on number of attendees
        const newNumberOfAttendees = Number(formValues.numberOfAttendees)
        const existingAttendees = props.initialValues?.attendees ?? []

        let attendees: AttendeeFormValues[]

        if (newNumberOfAttendees > existingAttendees.length) {
          // User increased the number of attendees
          const numberOfNewAttendeesAdded = newNumberOfAttendees - existingAttendees.length
          const newAttendees: AttendeeFormValues[] = Array.from({ length: numberOfNewAttendeesAdded }, () => ({
            fullName: '',
            isdId: isd != null ? isd.id : null,
            isAttendeeFromDistrict: null,
            districtId: null,
            status: null,
            completionStatus: null
          }))
          attendees = [...existingAttendees, ...newAttendees]
        } else if (newNumberOfAttendees < existingAttendees.length) {
          // User decreased the number of attendees
          attendees = existingAttendees.slice(0, newNumberOfAttendees)
        } else {
          // Number of attendees is unchanged
          attendees = existingAttendees
        }

        // If there were no existing attendees, create all new ones
        if (existingAttendees.length === 0) {
          attendees = Array.from({ length: newNumberOfAttendees }, () => ({ fullName: '', isdId: isd?.id ?? null, districtId: null, status: null, completionStatus: null, isAttendeeFromDistrict: null }))
        }
        setAdditionalDetailsFormValues({ attendees })
        return
      } else if (modalPage === 'details') {
        setAdditionalDetailsSubmitted(true)
        if (hasInvalidAdditionalDetailsFormValues) {
          toast.error('Please fill out required additional detail fields')
          return
        }
        const cleanAttendees = additionalDetailsFormValues.attendees.map(a => ({
          id: a.id,
          fullName: a.fullName,
          isdId: a.isdId,
          isAttendeeFromDistrict: a.districtId != null,
          districtId: a.districtId,
          status: a.status,
          completionStatus: a.completionStatus
        }))
        const epliTrainerDevelopmentPlan: UpdateEPLITrainerDevelopmentPlanFormValues = {
          id: props.initialValues?.id ?? 0,
          priorityId: props.initialValues?.priorityId ?? props.priorityId,
          numberOfAttendees: Number(formValues.numberOfAttendees),
          budgetRequest: Number(formValues.budgetRequest),
          epliTrainingId: formValues.epliTrainingId,
          isImplementationPhase: props.implementationPhase ?? false,
          isUpdate: props.initialValues?.isUpdate ?? false,
          attendees: cleanAttendees,
          actualAmount: props.initialValues?.actualAmount != null ? Number(props.initialValues.actualAmount) : null
        }
        // submit the updates
        const { success, errors } = await UpdateEPLITrainerDevelopmentPlan(epliTrainerDevelopmentPlan)
        if (!success) {
          errors.forEach(e => toast.error(e))
          return
        }

        toast.success('EPLI Trainer Plan Updated')
        props.onClose(props.initialValues?.isUpdate ?? false)
        props.onSaved()
      }
      return
    }

    setIsSubmitted(true)
    if (invalidFormValues.length > 0) {
      toast.error('Please fill out required fields')
      return
    }

    const { success, errors } = props.initialValues != null
      ? await UpdateEPLITrainerDevelopmentPlan({ ...formValues, id: props.initialValues.id })
      : await AddEPLITrainerDevelopmentPlan(formValues)

    if (!success) {
      errors.forEach(e => toast.error(e))
      return
    }

    toast.success(`Attendance Plan ${isEditing ? 'Updated' : 'Saved'}`)

    props.onClose(props.initialValues?.isUpdate ?? false)
    props.onSaved()
  }

  const selectedTraining = epliTrainingOptions.find((training) => training?.id != null && training.id.toString() === formValues.epliTrainingId?.toString())

  const getTitle = (): string => {
    let prefix = 'Add'
    if (props.implementationPhase === true && props.initialValues != null) prefix = 'Update'
    else if (isEditing) prefix = 'Edit'

    return `${prefix} EPLI Trainer Plan`
  }

  const handleClose = (): void => {
    if (props.implementationPhase === true && modalPage === 'details') {
      setShowCancelUpdatesModal(true)
    } else {
      props.onClose(props.initialValues?.isUpdate ?? false)
    }
  }

  const handleCancel = (): void => {
    if (props.implementationPhase === true) {
      if (modalPage === 'edit') props.onClose(props.initialValues?.isUpdate ?? false)
      else if (modalPage === 'details') {
        setModalPage('edit')
      }
    } else {
      props.onClose(props.initialValues?.isUpdate ?? false)
    }
  }

  const getDataTestId = (): string => {
    if (props.implementationPhase === true) return 'updateEPLITrainerDevelopmentPlanModal'
    return isEditing ? 'editEPLITrainerDevelopmentPlanModal' : 'addEPLITrainerDevelopmentPlanModal'
  }

  const handleUpdateAdditionalDetailsField = (index: number, e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<any>): void => {
    const { name, value } = e.target

    const updatedAttendees = [...additionalDetailsFormValues.attendees]
    if (name === 'orgTypeRadio') {
      if (value === 'No') {
        updatedAttendees[index] = { ...updatedAttendees[index], districtId: null }
      }
      updatedAttendees[index] = { ...updatedAttendees[index], isAttendeeFromDistrict: value === 'Yes' }
    } else {
      updatedAttendees[index] = { ...updatedAttendees[index], [name]: value }
    }
    setAdditionalDetailsFormValues({ ...additionalDetailsFormValues, attendees: updatedAttendees })
  }

  const handleConfirmCancelUpdates = async (): Promise<void> => {
    setShowCancelUpdatesModal(false)
    setIsSubmitted(true)

    const epliTrainerDevelopmentPlan: UpdateEPLITrainerDevelopmentPlanFormValues = {
      id: props.initialValues?.id ?? 0,
      priorityId: props.initialValues?.priorityId ?? props.priorityId,
      numberOfAttendees: Number(formValues.numberOfAttendees),
      budgetRequest: Number(formValues.budgetRequest),
      epliTrainingId: formValues.epliTrainingId,
      isImplementationPhase: props.implementationPhase ?? false,
      isUpdate: props.initialValues?.isUpdate ?? false,
      attendees: props.initialValues?.attendees ?? [],
      actualAmount: props.initialValues?.actualAmount != null ? Number(props.initialValues.actualAmount) : null
    }
    const { success, errors } = await UpdateEPLITrainerDevelopmentPlan(epliTrainerDevelopmentPlan)
    if (!success) {
      errors.forEach(e => toast.error(e))
      return
    }

    toast.success('EPLI Trainer Plan Partially Updated')
    props.onClose(props.initialValues?.isUpdate ?? false)
    props.onSaved()
  }

  if (showCancelUpdatesModal) {
    return (
      <Modal
        open={true}
        title='Cancel Updates'
        cancelButtonText='Go Back'
        confirmButtonText='Confirm Cancel'
        data-testid='confirmCancelUpdatesModal'
        bodyContent={<Stack gap='1rem' m='24px 0'>
          <Typography variant='body1' fontWeight={600}>Are you sure you want to cancel?</Typography>
          <Typography variant='body1'>If you proceed your changes in the Additional Details section will not be saved.</Typography>
        </Stack>}
        onClose={() => { setShowCancelUpdatesModal(false) }}
        onConfirm={handleConfirmCancelUpdates}
        maxWidth='xs'
      />
    )
  }

  return (confirmState === 'needsConfirmation'
    ? <BudgetThresholdExceededModal
        onClose={() => {
          setConfirmState('none')
          props.onClose(props.initialValues?.isUpdate ?? false)
        }}
        onConfirm={() => {
          setConfirmState('confirmed')
          void continueSubmit()
        }}
    />
    : <Modal
        title={getTitle()}
        open={props.open}
        data-testid={getDataTestId()}
        width='400px'
        confirmButtonText={props.implementationPhase === true ? 'Save & Continue' : 'Save'}
        cancelButtonText={props.implementationPhase === true && modalPage === 'details' ? 'Go Back' : 'Cancel'}
        onClose={handleClose}
        onCancel={handleCancel}
        onConfirm={handleSubmit}
        bodyContent={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {modalPage === 'edit' && (
              isLoading
                ? <CircularProgress />
                : <>
                  {props.implementationPhase !== true && (
                    <Typography
                      variant='body1'
                      sx={{
                        backgroundColor: 'var(--blue-50)',
                        border: '1px solid var(--blue-300)',
                        borderRadius: '8px',
                        padding: '10px 32px'
                      }}
                    >
                      You will have the opportunity to further define specific participant information during implementation.
                    </Typography>
                  )}
                  {
                    props.implementationPhase === true && props.initialValues == null &&
                      <Alert data-testid='contributorAlert' icon={<InfoOutlinedIcon fontSize="inherit" sx={{ fill: 'white' }} />} sx={{
                        backgroundColor: 'var(--blue-500)',
                        color: 'white',
                        fill: 'white'
                      }} severity="info"
                      >The addition of this item will prompt a review from the START team.
                      </Alert>
                  }
                  <Typography variant='h5'>EPLI Trainings</Typography>
                  <Typography variant='body1'>Please select the EPLI trainings that will be attended, enter the number of attendees along with the budget request.</Typography>
                  <FormControl
                    fullWidth
                    required={formValidators.epliTrainingId.required}
                    error={submitted && !formValidators.epliTrainingId.isValid}
                  >
                    <InputLabel id='training-label'>Training Name</InputLabel>
                    <Select
                      labelId='training-label'
                      id='training'
                      value={(epliTrainingOptions.length > 0 ? formValues.epliTrainingId : '') ?? ''}
                      label='Training Name'
                      name='epliTrainingId'
                      data-testid='training'
                      onChange={handleFieldUpdate as (e: SelectChangeEvent<typeof formValues.epliTrainingId>) => void}
                    >
                      {epliTrainingOptions.length === 0 && <Typography textAlign='center'>No trainings available.</Typography>}
                      {epliTrainingOptions.map((training) => (
                        <MenuItem key={training.id} value={training.id} data-testid='trainingOption'>{training.title}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {selectedTraining != null &&
                    <EventDisplay
                      eventBoxStyle={{ width: '2.25em', height: '3.375em', my: '.5em' }}
                      event={{ title: selectedTraining.title, date: selectedTraining.meetingDate }}
                      description={selectedTraining.description}
                      seriesLetter='E'
                    />
                }

                  <TextField
                    name='numberOfAttendees'
                    label='Number Attending'
                    value={formValues.numberOfAttendees ?? ''}
                    data-testid='numberOfAttendees'
                    onChange={handleFieldUpdate}
                    variant='filled'
                    required={formValidators.numberOfAttendees.required}
                    error={submitted && !formValidators.numberOfAttendees.isValid}
                  />

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <Typography variant='body1'>Does this require additional funding to complete?</Typography>
                    <TextField
                      id='budget-request'
                      sx={{ mt: '.75em', width: '250px' }}
                      variant='filled'
                      onChange={handleFieldUpdate}
                      label={'Budget Request'}
                      data-testid='budgetRequest'
                      InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                      name='budgetRequest'
                      required={formValidators.budgetRequest.required}
                      error={submitted && !formValidators.budgetRequest.isValid}
                      value={formValues.budgetRequest ?? ''}
                    />
                  </Box>

                  {props.initialValues != null &&
                    <ActivityEditSession
                      activityId={props.initialValues.id}
                      activityName={PriorityOneActivityName.EpliPlan}
                      onClose={() => { props.onClose(props.initialValues?.isUpdate ?? false) }}
                    />
                }
                </>
            )}

            {modalPage === 'details' && (
              <Stack gap='1rem' py='.5rem'>
                <Typography variant='h4'>Additional Details</Typography>

                {/* Loop through number of attendees */}
                {additionalDetailsFormValues.attendees.length === 0 && (
                  <Typography variant='body1'>No attendees have been added to this plan.</Typography>
                )}
                {additionalDetailsFormValues.attendees.map((attendee, index) => (
                  <Stack key={index} gap='1rem'>
                    <Typography variant='body1' fontWeight={600}>Attendee ({index + 1})</Typography>
                    {/* Attendee full name field */}
                    <TextField
                      id={`attendeeFullName-${index}`}
                      name='fullName'
                      label='Attendee Full Name'
                      value={attendee.fullName}
                      data-testid={`attendeeFullName-${index}`}
                      onChange={(e) => { handleUpdateAdditionalDetailsField(index, e) }}
                      variant='filled'
                      required={attendeeFormValidator(index).fullName.required}
                      error={additionalDetailsSubmitted && !attendeeFormValidator(index).fullName.isValid}
                    />

                    <FormControl
                      sx={{ alignContent: 'center', width: '100%' }}
                      data-testid='formControlOrgRadio'
                      required={true}
                      error={additionalDetailsSubmitted && !attendeeFormValidator(index).isAttendeeFromDistrict.isValid}
                    >
                      <FormLabel>Is attendee part of a District?</FormLabel>
                      <RadioGroup
                        row={true}
                        aria-labelledby="scope-radio-buttons-group"
                        name="orgTypeRadio"
                        value={attendee.isAttendeeFromDistrict === true ? 'Yes' : 'No'}
                        onChange={(e) => { handleUpdateAdditionalDetailsField(index, e) }}
                      >
                        <FormControlLabel data-testid={`${index}districtRadio`} value="Yes" control={<Radio />} label="Yes" />
                        <FormControlLabel data-testid={`${index}isdRadio`} value="No" control={<Radio />} label="No" />
                      </RadioGroup>
                    </FormControl>

                    {/* District field */}
                    {
                      attendee.isAttendeeFromDistrict !== true
                        ? null
                        : isLoadingPlanOrgOrgs
                          ? <CircularProgress sx={{ alignSelf: 'center' }} />
                          : <FormControl
                              fullWidth
                              required={attendeeFormValidator(index).districtId.required}
                              error={additionalDetailsSubmitted && !attendeeFormValidator(index).districtId.isValid}
                          >
                            <InputLabel id="districtLabel">District</InputLabel>
                            <Select
                              labelId="districtLabel"
                              id={`district-${index}`}
                              value={districts?.some(d => d.id === additionalDetailsFormValues.attendees[index].districtId) === true ? additionalDetailsFormValues.attendees[index].districtId : ''}
                              label="District"
                              name='districtId'
                              data-testid={`attendeeDistrict-${index}`}
                              onChange={(e) => { handleUpdateAdditionalDetailsField(index, e) }}
                            >
                              {districts?.length === 0 && <Typography variant='body1' textAlign={'center'}>No district options</Typography>}
                              {(districts?.length ?? 0) > 0 && districts?.map((district) => (
                                <MenuItem key={district.id} value={district.id} data-testid='attendeeDistrictOption'>{district.name}</MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                    }

                    {/* Current attendee EPLI status radio group */}
                    <FormControl
                      variant='filled'
                      required={attendeeFormValidator(index).status.required}
                      error={additionalDetailsSubmitted && !attendeeFormValidator(index).status.isValid}
                    >
                      <FormLabel id={`statusLabel-${index}`}>What is the attendee's current EPLI Trainer status?</FormLabel>
                      <RadioGroup
                        aria-labelledby={`statusLabel-${index}`}
                        value={attendee.status ?? ''}
                        id={`status-${index}`}
                        name='status'
                        data-testid={`attendeeStatus-${index}`}
                        onChange={(e) => { handleUpdateAdditionalDetailsField(index, e) }}
                      >
                        {Object.entries(EPLITrainerStatus).map(([key, status]) => (
                          <FormControlLabel
                            key={key}
                            data-testid={`attendeeStatusOption-${index}-${key}`}
                            value={status}
                            control={<Radio />}
                            label={status}
                            sx={{
                              '& .MuiSvgIcon-root': {
                                fontSize: 14
                              }
                            }}
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>
        }
    />
  )
}
